/**
 * Phase 24.4b Step 6 — week header with date range + nav + regenerate.
 *
 * Backend signal / frontend voice: warnings come back as i18n keys
 * (S2c convention). Rendered as a comma-joined list via t().
 */
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { fmtDate } from "@/lib/dates";

interface Props {
  weekStartDate: string; // ISO YYYY-MM-DD
  warnings: string[];
  onPrevWeek?: () => void;
  onNextWeek?: () => void;
  onRegenerate?: () => void;
  isRegenerating?: boolean;
}

export function WeekHeader({
  weekStartDate,
  warnings,
  onPrevWeek,
  onNextWeek,
  onRegenerate,
  isRegenerating = false,
}: Props) {
  const { t } = useTranslation();
  const start = new Date(weekStartDate);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const startLabel = fmtDate(start.toISOString(), "d MMM");
  const endLabel = fmtDate(end.toISOString(), "d MMM");

  return (
    <header className="space-y-2" data-testid="week-header">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {onPrevWeek ? (
            <button
              type="button"
              onClick={onPrevWeek}
              aria-label={t("meal_plan.prev_week")}
              className="rounded-md border border-cream-border bg-cream-light p-1.5 text-navy hover:bg-cream-border/50"
              data-testid="week-prev"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={2} />
            </button>
          ) : null}
          <h2 className="font-serif text-[20px] font-medium leading-snug text-navy-deep">
            {t("meal_plan.week_range", { start: startLabel, end: endLabel })}
          </h2>
          {onNextWeek ? (
            <button
              type="button"
              onClick={onNextWeek}
              aria-label={t("meal_plan.next_week")}
              className="rounded-md border border-cream-border bg-cream-light p-1.5 text-navy hover:bg-cream-border/50"
              data-testid="week-next"
            >
              <ChevronRight className="h-4 w-4" strokeWidth={2} />
            </button>
          ) : null}
        </div>
        {onRegenerate ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={onRegenerate}
            disabled={isRegenerating}
            data-testid="week-regenerate"
          >
            <RefreshCw
              className={isRegenerating ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"}
              strokeWidth={2}
            />
            {isRegenerating
              ? t("meal_plan.regenerating")
              : t("meal_plan.regenerate")}
          </Button>
        ) : null}
      </div>
      {warnings.length > 0 ? (
        <div
          className="rounded-md border-l-4 border-amber bg-amber/10 px-3 py-2 text-[12px] text-navy-deep"
          data-testid="week-warnings"
        >
          {warnings.map((key) => t(key)).join(" · ")}
        </div>
      ) : null}
    </header>
  );
}
